import { CapTableRail, TransactionTitle } from '@dealstack/shared/lib/generated';

/**
 * The rail an inseparable redemption is resolved against, or null when the title is not a single
 * rail.
 *
 * Total by construction, and that is the whole point. The operation editor previously mapped the
 * three-valued title onto the two-valued rail with a two-branch ternary, so "Both" fell into the
 * else and became BENEFICIAL. That equalled the stored value, so the PATCH was a no-op that
 * returned 200 and toasted "Transaction updated successfully" while the register never moved. A
 * coerced value is indistinguishable from success, which is why an unmappable title has to come
 * back as null and be refused out loud.
 */
export const railFromTitle = (title: TransactionTitle | null | undefined): CapTableRail | null => {
	if (title === TransactionTitle.LEGAL_TITLE) {
		return CapTableRail.LEGAL;
	}
	if (title === TransactionTitle.BENEFICIAL_INTEREST) {
		return CapTableRail.BENEFICIAL;
	}
	return null;
};

export interface InterestOption {
	value: TransactionTitle;
	label: string;
}

/**
 * The interests a leg of an interest PAIR may be given.
 *
 * Both stays: that is the merge, the inverse of the split. The other rail goes, because a pair
 * cannot carry the same interest twice and the rule refuses it however the legs are ordered.
 * Offering it anyway was a dead end -- the save raised a dialog whose only way out was deleting the
 * pair, and closing it left the rejected rail in the field so the next save raised the identical
 * dialog.
 *
 * Unpaired rows, and rows whose interest is Both, keep the full set.
 */
export const interestOptionsForPairedLeg = (
	options: InterestOption[],
	title: TransactionTitle | null | undefined,
	interestPairId: string | null | undefined
): InterestOption[] => {
	if (!interestPairId || !railFromTitle(title)) {
		return options;
	}
	return options.filter(
		(option) =>
			option.value === title ||
			option.value === TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE
	);
};
